import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  formatGermanDate,
  formatSequenceNumber,
  type BeaconCard,
  type GeneratedItem,
  type MeasurementTable,
  type SummaryFigures,
  type UserFeatures,
} from '@oscom/shared';
import { floorPlanImageUrl } from '../api/floor-plans';
import { ratingClass } from '../plans/markers';
import { PlanFigureView } from './PlanFigureView';
import { RatingLegend } from './RatingLegend';
import { dbmText } from './RatingMatrix';

const LEVELS_DESC = [5, 4, 3, 2, 1] as const;

/** Which areas a link may lead to (their feature switches). */
export type GeneratedLinkTargets = Pick<
  UserFeatures,
  'floorPlans' | 'technicalObjects' | 'measurements'
>;

/** Content of a generated chapter: notices, plans, beacon cards, tables, figures. */
export function GeneratedContentView({
  items,
  customerId,
  links,
}: {
  items: readonly GeneratedItem[];
  customerId: string;
  /**
   * Editor: the open areas, linked to the objects there (a closed area gets
   * plain text, its pages would be NotFound); preview and print: false.
   */
  links: GeneratedLinkTargets | false;
}): ReactNode {
  const base = `/kunden/${encodeURIComponent(customerId)}`;
  const linkPlans = links !== false && links.floorPlans;
  const linkObjects = links !== false && links.technicalObjects;
  const linkMeasurements = links !== false && links.measurements;
  return (
    <div className="generated">
      {items.map((item, index) => {
        switch (item.kind) {
          case 'notice':
            return (
              <p key={index} className="notice notice--warning">
                {item.text}
              </p>
            );
          case 'plans':
            return (
              <div key={index} className="plan-figures">
                {item.plans.map((figure) => (
                  <PlanFigureView
                    key={`${figure.planId}:${figure.image.assetId}`}
                    figure={figure}
                    imageUrl={floorPlanImageUrl(customerId, figure.planId, figure.image.assetId)}
                    href={
                      linkPlans ? `${base}/plaene/${encodeURIComponent(figure.planId)}` : undefined
                    }
                  />
                ))}
              </div>
            );
          case 'beaconCards':
            return (
              <div key={index} className="beacon-cards">
                {item.cards.map((card) => (
                  <BeaconCardView
                    key={card.beaconId}
                    card={card}
                    href={linkObjects ? `${base}/objekte` : undefined}
                  />
                ))}
              </div>
            );
          case 'measurementTable':
            return (
              <MeasurementTableView
                key={index}
                table={item.table}
                href={
                  linkMeasurements
                    ? `${base}/messungen/${encodeURIComponent(item.table.seriesId)}`
                    : undefined
                }
              />
            );
          case 'summary':
            return <SummaryView key={index} summary={item.summary} />;
          default:
            return null;
        }
      })}
    </div>
  );
}

function BeaconCardView({ card, href }: { card: BeaconCard; href: string | undefined }): ReactNode {
  const rows: Array<[string, string | undefined]> = [
    ['Nr.', formatSequenceNumber(card.sequenceNumber)],
    ['Ort', card.locationLabel],
    ['Position', card.positionDescription],
    ['Montagehinweis', card.mountingNote],
    ['Seriennummer', card.serialNumber],
    ['MAC', card.macAddress],
    ['UUID', card.uuid],
  ];
  return (
    <article className="beacon-card">
      <h4 className="beacon-card__name">
        {href ? <Link to={href}>{card.beaconName}</Link> : card.beaconName}
      </h4>
      <dl className="beacon-card__data">
        {rows
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
      </dl>
    </article>
  );
}

function MeasurementTableView({
  table,
  href,
}: {
  table: MeasurementTable;
  href: string | undefined;
}): ReactNode {
  return (
    <div className="measurement-table">
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Nr.</th>
              <th scope="col">Ortstext</th>
              <th scope="col">Ort</th>
              {table.columns.map((column) => (
                <th key={column.label} scope="col">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row) => (
              <tr key={row.pointId}>
                <td>{href ? <Link to={href}>{row.sequenceNumber}</Link> : row.sequenceNumber}</td>
                <td>{row.locationNote ?? ''}</td>
                <td>{row.locationLabel}</td>
                {row.cells.map((cell, index) => (
                  <td
                    key={index}
                    className={`rating ${ratingClass(cell.level)}`}
                    title={cell.level === null ? 'Kein Empfang' : `Stufe ${cell.level}`}
                  >
                    {cell.rsrp === null ? '---' : dbmText(cell.rsrp)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RatingLegend />
    </div>
  );
}

function SummaryView({ summary }: { summary: SummaryFigures }): ReactNode {
  return (
    <div className="doc-summary">
      {summary.beaconCount !== null ? <p>Beacons im Umfang: {summary.beaconCount}</p> : null}
      {summary.series.map((entry) => (
        <div key={entry.seriesId} className="doc-summary__series">
          <p>
            <strong>{entry.label}</strong> ({formatGermanDate(entry.measuredOn)}):{' '}
            {entry.pointCount} Messpunkte
          </p>
          {entry.distribution.length > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Anbieter und Technik</th>
                    {LEVELS_DESC.map((level) => (
                      <th key={level} scope="col" className={`rating rating--${level}`}>
                        Stufe {level}
                      </th>
                    ))}
                    <th scope="col">Kein Empfang</th>
                  </tr>
                </thead>
                <tbody>
                  {entry.distribution.map((row) => (
                    <tr key={row.column.label}>
                      <th scope="row">{row.column.label}</th>
                      {LEVELS_DESC.map((level) => (
                        <td key={level}>{row.byLevel[level]}</td>
                      ))}
                      <td>{row.none}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
